/**
 * Stałe drugiego projektu Matomo — analityki zbudowanej wokół `idTransRemote` (PEP-1380).
 * Jedynym odbiorcą jest `utils/transactionTracking.js`; komponenty przekazują wyłącznie `TRX_STEP`.
 * → `doc/dokumentacja/matomo.md` §10
 */

/**
 * Numery wymiarów to `idcustomdimension` z konfiguracji projektu w Matomo (dla trackera JS to
 * parametr `dimension<N>`), nie indeks slotu. Projekty STG (site 66, 12.10.2026) i produkcyjny
 * (site 67, 15.10.2026) mają wymiary w tej samej kolejności — każdy kolejny musi ją powtórzyć.
 *
 * Wymiar 5 do PEP-1420 nosił nazwę `wersja` (`VITE_APP_RELEASE`); slotów wizyty jest pięć i wszystkie
 * były zajęte, więc `orderId` przejął jego numer. Wartości sprzed zmiany to hashe wydań.
 */
export const TRX_DIMENSION = {
  // zakres wizyty
  visitIdTransRemote: 1,
  serviceId: 2,
  context: 3,
  clientEnv: 4,
  // numer zamówienia sklepu — łączy kolejne próby płatności jednego zamówienia (PEP-1420)
  orderId: 5,
  // zakres akcji
  actionIdTransRemote: 6,
  step: 7,
  payway: 8,
  group: 9,
  state: 10,
}

/** Jedyna kategoria zdarzeń w projekcie: akcja = krok, nazwa = szczegół kroku. */
export const TRX_EVENT_CATEGORY = 'transakcja'

/**
 * Kroki iteracji 1 — minimum pozwalające odtworzyć ścieżkę transakcji. Każdy nowy krok musi
 * odpowiadać na pytanie „jakiemu stanowi w bazie to odpowiada?”.
 */
export const TRX_STEP = {
  // odsłona trasy (tytuł = nazwa trasy) — wysyła ją sam moduł z `router.afterEach`
  view: 'widok',
  // backend przyjął zlecenie płatności (udane `update-payway`)
  paymentOrdered: 'platnosc_zlecona',
  // dokument jest zamykany albo opuszczany (bank, 3DS, sklep) — wysyła sam moduł na `pagehide`
  documentLeave: 'opuszczenie_dokumentu',
  // stan transakcji znany frontowi (`/confirmation`, `/result`)
  result: 'wynik',
  // widok błędu (nazwa = kod błędu) — tylko w dokumencie, który zna `idTransRemote`
  error: 'blad',
  // zamówienie ecommerce — wynik pozytywny w walucie projektu (`TRX_ORDER_CURRENCY`)
  order: 'zamowienie',
  // transakcję objęto mechanizmem ograniczenia ekspozycji kart (PaymentBoost) — paywall z aktywnym
  // mechanizmem; nazwa = `serviceId` (PEP-1424; akcja jak w projekcie 47/48)
  cardLimitActive: 'card_depreciation_mechanism_active',
  // płatnik kliknął „pokaż więcej metod” i odkrył schowane grupy (PEP-1424; akcja jak
  // w projekcie 47/48)
  cardLimitRevealed: 'card_depreciation_mechanism_show_groups',
  // interakcja z formularzem przed próbą płatności (PEP-1425) — nazwy jak w kategorii `formularz`
  // projektu 47/48; bez wartości pól, `name` tylko przy zgodach (`agreement.code`)
  emailFilled: 'wypelniono_email',
  blikFilled: 'wypelniono_blik',
  regulationChecked: 'zaznaczono_regulamin',
  regulationUnchecked: 'odznaczono_regulamin',
  agreementChecked: 'zaznaczono_zgode',
  agreementUnchecked: 'odznaczono_zgode',
  // lejek Click to Pay (PEP-1426) — akcje z kategorii `c2p` projektu 47/48 pod nazwami z prefiksem
  // `c2p_` (kategoria w projekcie transakcyjnym jest jedna); bez wartości pól (e-mail, kod OTP,
  // dane karty), `name` tylko przy organizacji karty i kanale ponownego kodu
  c2pLoginView: 'c2p_widok_logowania',
  c2pCodeView: 'c2p_widok_kodu',
  c2pCardsView: 'c2p_widok_kart',
  c2pCardSelected: 'c2p_wybrano_karte',
  c2pCodeSent: 'c2p_wyslano_kod',
  c2pCodeResent: 'c2p_ponownie_wyslano_kod',
  c2pCardChanged: 'c2p_zmieniono_karte',
  c2pNewUser: 'c2p_nowy_uzytkownik',
  // stany i przejścia C2P, których stara integracja nie mierzyła (PEP-1426) — ten sam prefiks `c2p_`
  // awaria procesu C2P; name = etap (`sdk`, `rozpoznanie`, `checkout`), po niej proces schodzi na zwykłą kartę
  c2pError: 'c2p_blad',
  // wynik rozpoznania e-maila; name = `rozpoznany` / `nierozpoznany`
  c2pRecognition: 'c2p_rozpoznanie',
  // kod z formularza przyjęty przez SDK (płatnik zalogowany)
  c2pCodeAccepted: 'c2p_kod_zweryfikowany',
  // kod odrzucony; name = `niepoprawny`, `konto_zablokowane`, `nieznany_blad` albo `limit_prob`
  c2pCodeRejected: 'c2p_kod_odrzucony',
  // ekran z wybraną kartą (gotowy do zapłaty) i ekran przewalutowania C2P
  c2pSelectedCardView: 'c2p_widok_wybranej_karty',
  c2pDccView: 'c2p_widok_dcc',
  // kliknięcia powrotne i zmiany ścieżki
  c2pSelectedCardChange: 'c2p_zmiana_wybranej_karty',
  c2pNewCardChosen: 'c2p_nowa_karta',
  c2pLoginChosen: 'c2p_wybrano_logowanie',
  c2pEmailChange: 'c2p_zmiana_emaila',
  c2pLoginBack: 'c2p_wstecz_z_logowania',
  // lejek Apple Pay (PEP-1427) — akcje z kategorii `apple_pay` projektu 47/48 z prefiksem `ap_`;
  // `name` = typ obsługi (`natywna` / `nienatywna` / `nieznana`), nie osobne zdarzenie
  apPaymentStarted: 'ap_rozpoczeto_platnosc',
  apPaymentAuthorized: 'ap_autoryzowano_platnosc',
  apPaymentCancelled: 'ap_anulowano_platnosc',
  apPaymentError: 'ap_blad_platnosci',
  // stany i przejścia Apple Pay, których stara integracja nie mierzyła (PEP-1427)
  // przycisk Apple Pay pokazany (name = typ obsługi) albo nie (name = przyczyna: `brak_api`,
  // `brak_wsparcia`, `blad_wykrywania`)
  apButtonAvailable: 'ap_przycisk_dostepny',
  apButtonUnavailable: 'ap_przycisk_niedostepny',
  // klik przy niepoprawnym formularzu — sesja się nie zaczyna, więc nie ma `ap_rozpoczeto_platnosc`
  apFormInvalid: 'ap_klik_formularz_niepoprawny',
  // Apple pokazało arkusz (`onvalidatemerchant`); `ap_rozpoczeto_platnosc` idzie przed `begin()`
  apSheetOpened: 'ap_arkusz_otwarty',
  // etap, na którym padła płatność (name = `start_sesji`, `walidacja_merchanta`, `brak_tokenu`,
  // `realizacja`); idzie obok `ap_blad_platnosci`, które niesie typ obsługi — tu typu obsługi nie ma
  apErrorStage: 'ap_blad_etap',
  // lejek Google Pay (PEP-1438) — lustro `ap_*`, ale bez odpowiednika w projekcie 47/48 (tam Google Pay
  // nie miał własnych zdarzeń); Google nie rozróżnia typu obsługi, więc `name` niesie wyłącznie przyczynę
  // albo etap, a kroki cyklu życia płatności idą bez `name`
  // klik wpuszczony do Google (arkusz się otwiera) — odpowiednik `ap_rozpoczeto_platnosc`
  gpPaymentStarted: 'gp_rozpoczeto_platnosc',
  // płatnik zatwierdził płatność w arkuszu (`onPaymentAuthorized`), przed `update-payway`
  gpPaymentAuthorized: 'gp_autoryzowano_platnosc',
  // arkusz zamknięty bez płatności (`cancel`)
  gpPaymentCancelled: 'gp_anulowano_platnosc',
  // awaria płatności; `gp_blad_etap` mówi, na którym etapie
  gpPaymentError: 'gp_blad_platnosci',
  // etap awarii (name = `arkusz`, `realizacja`), obok `gp_blad_platnosci`
  gpErrorStage: 'gp_blad_etap',
  // przycisk Google Pay pokazany albo nie (name = przyczyna: `odmowa_isready`, `brak_odpowiedzi`,
  // `blad_konfiguracji`)
  gpButtonAvailable: 'gp_przycisk_dostepny',
  gpButtonUnavailable: 'gp_przycisk_niedostepny',
  // klik przy niepoprawnym formularzu — arkusz się nie otwiera
  gpFormInvalid: 'gp_klik_formularz_niepoprawny',
  // klik zatrzymany strażnikiem `SHEET_GUARD_MS` — poprzedni arkusz nie oddał rozstrzygnięcia
  gpClickBlocked: 'gp_klik_zatrzymany',
  // `update-payway` bez odpowiedzi przed limitem Google; arkusz zamykamy sukcesem, więc to nie jest
  // `gp_blad_platnosci`
  gpAuthorizationTimeout: 'gp_limit_autoryzacji',
}

/**
 * Kroki, po których dokument zwykle zaraz znika (wyjście do banku, zamknięcie strony). Idą beaconem
 * z pominięciem 800 ms odstępu trackera Matomo — inaczej giną razem z dokumentem.
 * → `utils/transactionTracking.js` (`sendEventBeacon`)
 */
export const TRX_LEAVING_STEPS = [TRX_STEP.paymentOrdered, TRX_STEP.documentLeave]

/**
 * Nazwa kroku `blad`, gdy dane błędu nie niosą kodu (np. `/update-payway` odrzucone bez ciała JSON —
 * odpowiedź HAProxy albo zerwana sieć). Ze statusem HTTP nazwą jest `HTTP_<status>`, bez niego —
 * `BRAK_ODPOWIEDZI`; puste zdarzenie nie mówiło, co poszło nie tak (~7 % kroków `blad`, pomiar 15.10.2026).
 * Bez kodu i bez statusu = żądanie nie dostało odpowiedzi (każde miejsce bez statusu podaje kod jawnie);
 * Sentry tych awarii nie widzi (`Failed to fetch`/`Load failed` w `ignoreErrors`), więc to ich jedyna miara.
 */
export const TRX_ERROR_HTTP_PREFIX = 'HTTP_'
export const TRX_ERROR_NO_RESPONSE = 'BRAK_ODPOWIEDZI'

/**
 * Waluta projektu w Matomo. Zamówienie ecommerce idzie WYŁĄCZNIE dla transakcji w tej walucie —
 * projekt ma jedną walutę, a bramka obsługuje płatności walutowe, więc inne kwoty zmieszałyby się
 * w jednym przychodzie. DCC nie zmienia waluty transakcji (przewalutowanie dotyczy tylko płatnika).
 */
export const TRX_ORDER_CURRENCY = 'PLN'

/** Wartości wymiaru `srodowisko_klienta`. */
export const TRX_CLIENT_ENV = {
  sdkAndroid: 'sdk_android',
  sdkIos: 'sdk_ios',
  webviewAndroid: 'webview_android',
  web: 'web',
}

/** Zabezpieczenie przed pętlą: tyle zdarzeń czeka najwyżej na identyfikację transakcji. */
export const TRX_QUEUE_LIMIT = 50

/** Tyle czekamy na `piwik.js` (ładowany async z `index.html`), zanim uznamy, że go nie będzie. */
export const TRX_TRACKER_WAIT_MS = 15000
export const TRX_TRACKER_POLL_MS = 100
